/**
 * @file        src/components/DatePicker.jsx
 * @description Themed date picker that replaces the native <input type="date">
 *              across admin forms. Light/glass admin theme, with custom
 *              month + year dropdowns (no OS calendar). Controlled: parent owns
 *              `value` as a 'YYYY-MM-DD' string; onChange(key) fires on pick.
 * @author      Kinetika Labs
 * @created     2026-07-02
 * @changelog
 *   - 2026-08-04 Claude — Optional `min` prop ('YYYY-MM-DD'): days before it
 *                          render disabled (wallet credit expiry must not be
 *                          in the past).
 */
import { useEffect, useMemo, useRef, useState } from 'react';

const WD = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const pad = (n) => String(n).padStart(2, '0');
const toKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
function fromKey(s) {
  if (!s) return null;
  const [y, m, d] = String(s).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}
const sameDay = (a, b) => !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
const addMonths = (d, n) => new Date(d.getFullYear(), d.getMonth() + n, 1);
// dd-mm-yyyy display, matching the native placeholder the admin used before.
function fmtField(s) {
  const d = fromKey(s);
  return d ? `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}` : '';
}

export default function DatePicker({ value, onChange, placeholder = 'dd-mm-yyyy', invalid = false, min = '' }) {
  const today = useMemo(() => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), t.getDate()); }, []);
  const minDate = fromKey(min);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(null); // 'month' | 'year' | null
  const [alignRight, setAlignRight] = useState(false); // flip when the field sits near the right edge
  const [base, setBase] = useState(() => fromKey(value) || today);
  const ref = useRef(null);
  const menuRef = useRef(null);

  // The popup is 288px wide; if opening left-anchored would overflow the viewport,
  // anchor it to the field's right edge instead (e.g. the last column of a filter row).
  const POP_W = 288;
  const openAt = () => {
    setBase(fromKey(value) || today);
    setMenu(null);
    if (ref.current) {
      const r = ref.current.getBoundingClientRect();
      setAlignRight(r.left + POP_W > window.innerWidth - 12 && r.right - POP_W >= 12);
    }
    setOpen((o) => !o);
  };

  const sel = fromKey(value);
  const year = base.getFullYear();
  const month = base.getMonth();

  const years = useMemo(() => {
    const from = today.getFullYear() + 5;
    const to = today.getFullYear() - 100;
    const out = [];
    for (let y = from; y >= to; y -= 1) out.push(y);
    return out;
  }, [today]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!ref.current?.contains(e.target)) { setOpen(false); setMenu(null); } };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); setMenu(null); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  useEffect(() => {
    if (menu && menuRef.current) {
      const el = menuRef.current.querySelector('[data-active="true"]');
      if (el) el.scrollIntoView({ block: 'center' });
    }
  }, [menu]);

  const firstDow = new Date(year, month, 1).getDay();
  const daysIn = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i += 1) cells.push(null);
  for (let d = 1; d <= daysIn; d += 1) cells.push(new Date(year, month, d));

  const jump = (y, m) => setBase(new Date(y, m, 1));
  const pick = (d) => { onChange(toKey(d)); setOpen(false); setMenu(null); };

  return (
    <div className={`ndp${open ? ' open' : ''}`} ref={ref}>
      <button type="button" className={`ndp-btn${invalid ? ' invalid' : ''}`} onClick={openAt}>
        <span className={value ? '' : 'ndp-ph'}>{fmtField(value) || placeholder}</span>
        <svg viewBox="0 0 24 24" className="ndp-cal" aria-hidden="true"><path fill="currentColor" d="M7 2v2H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2V2h-2v2H9V2zm12 7v10H5V9z" /></svg>
      </button>

      {open && (
        <div className={`ndp-pop${alignRight ? ' right' : ''}`}>
          <div className="ndp-head">
            <button type="button" className="ndp-nav" onClick={() => setBase(addMonths(base, -1))} aria-label="Previous month">
              <svg viewBox="0 0 24 24"><path fill="currentColor" d="M15 6 9 12l6 6z" /></svg>
            </button>
            <div className="ndp-sel-wrap">
              <button type="button" className={`ndp-sel${menu === 'month' ? ' active' : ''}`} onClick={() => setMenu((m) => (m === 'month' ? null : 'month'))}>
                {MONTHS[month]}<svg viewBox="0 0 24 24"><path fill="currentColor" d="m7 10 5 5 5-5z" /></svg>
              </button>
              <button type="button" className={`ndp-sel${menu === 'year' ? ' active' : ''}`} onClick={() => setMenu((m) => (m === 'year' ? null : 'year'))}>
                {year}<svg viewBox="0 0 24 24"><path fill="currentColor" d="m7 10 5 5 5-5z" /></svg>
              </button>
              {menu && (
                <div className={`ndp-menu${menu === 'year' ? ' right' : ''}`} ref={menuRef}>
                  {(menu === 'month' ? MONTHS.map((label, val) => ({ val, label })) : years.map((y) => ({ val: y, label: String(y) }))).map((o) => {
                    const active = menu === 'month' ? o.val === month : o.val === year;
                    return (
                      <button key={o.val} type="button" data-active={active ? 'true' : undefined}
                        className={`ndp-menu-opt${active ? ' sel' : ''}`}
                        onClick={() => { if (menu === 'month') jump(year, o.val); else jump(o.val, month); setMenu(null); }}>
                        {o.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button type="button" className="ndp-nav" onClick={() => setBase(addMonths(base, 1))} aria-label="Next month">
              <svg viewBox="0 0 24 24"><path fill="currentColor" d="M9 6l6 6-6 6z" /></svg>
            </button>
          </div>

          <div className="ndp-wd">{WD.map((w) => <span key={w}>{w}</span>)}</div>
          <div className="ndp-grid">
            {cells.map((d, i) => {
              if (d === null) return <span key={`e${i}`} />;
              const disabled = !!minDate && d < minDate;
              return (
                <button key={toKey(d)} type="button" disabled={disabled}
                  className={`ndp-day${sameDay(d, sel) ? ' sel' : ''}${sameDay(d, today) ? ' today' : ''}`}
                  onClick={() => pick(d)}>
                  {d.getDate()}
                </button>
              );
            })}
          </div>

          <div className="ndp-foot">
            <button type="button" className="ndp-link" onClick={() => { onChange(''); setOpen(false); }}>Clear</button>
            <button type="button" className="ndp-link" onClick={() => pick(today)}>Today</button>
          </div>
        </div>
      )}
    </div>
  );
}
